import { debounce, uniqueId } from 'lodash-es';
import { makeAutoObservable, reaction } from 'mobx';
import { ReactNode } from 'react';
import { ApiaUtil, ApiaUtilTooltip } from '../../../../objects';
import { AutocompleteContext } from '../Context';
import { OptionsBox } from '../OptionsBox';

export type AutocompleteProps = {
  /**
   * Si este flag es true, la búsqueda se realiza con .toLowerCase() en el valor y en la cadena de búsqueda.
   */
  caseInsensitive?: boolean;
  onChange?: (value: string, controller: AutocompleteController) => unknown;
  /**
   * Si se pasa una función de búsqueda, cuando el usuario busque se llamará a esta función para determinar qué opciones deberán mostrarse. Las opciones devueltas deberán tener filtered !== true para mostrarse.
   *
   * La función de búsqueda será llamada mediante un debounce con tiempo = state.searchDebounce (ms).
   *
   * Si no se pasa función de búsqueda, la comparación se realizará contra this.state.options, siguiendo el algoritmo descrito en AutocompleteOption.
   *
   */
  onSearch?: (
    str: string,
    controller: AutocompleteController,
  ) => Promise<AutocompleteOption[]>;
  options: AutocompleteOption[];
  /**
   * Por defecto es 100ms.
   */
  searchDebounce?: number;
} & AutocompleteBoxProps &
  Partial<Pick<AutocompleteState, 'disabled' | 'value'>>;

type AutocompleteBoxProps = Partial<{
  className: string;
  id: string;
  name: string;
}>;

type AutocompleteState = {
  disabled: boolean;
  focusedValue?: null | string;
  loading: boolean;
  showValue: string;
  value: string;
};

/**
 * El componente buscará dentro de las opciones disponibles cuando el usuario realice una búsqueda en el cuadro de texto. Para ello, compara cada opción contra str según:
 *
 * Si caseInsensitive===true, la comparación se hará en lowerCase.
 *
 * Si se pasa searchLabel, se compara contra searchLabel.
 * Sino, si label?.toString() está definido, se utiliza label?.toSring().
 * Sino, se utiliza value.
 *
 * Un se mostrará en los resultados si el algoritmo determina que (result_search_label).indexOf(str) !== -1.
 */
export type AutocompleteOption = {
  /**
   * @private
   *
   * Se utiliza para determinar si debe mostrarse o no.
   */
  filtered?: boolean;
  label: ReactNode;
  /**
   * Si se pasa searchLabel, será utilizado en vez de label. Si no se pasa, se hará label.toString() para realizar la búsqueda, que solamente tiene sentido cuando label es un primitivo.
   */
  searchLabel?: string;
  /**
   * @default Por defecto toma el valor ''
   */
  value: string;
};

export class AutocompleteController {
  state: AutocompleteState & AutocompleteProps = {
    disabled: false,
    id: uniqueId('autocomplete'),
    loading: false,
    options: [],
    searchDebounce: 100,
    showValue: '',
    value: '',
  };

  constructor(properties: Partial<AutocompleteProps>) {
    Object.assign(this.state, properties);
    if (properties.value) {
      this.state.showValue = properties.value;
      this.state.focusedValue = properties.value;
    }

    makeAutoObservable(this, {
      getAutocompleteBox: false,
      getOptionByValue: false,
    });

    reaction(
      () => this.state.searchDebounce,
      () => {
        this.#search = this.#makeSearch();
      },
    );

    reaction(
      () => this.state.focusedValue,
      () => {
        if (this.state.focusedValue) {
          this.getOptionElement(this.state.focusedValue)?.scrollIntoView({
            block: 'nearest',
            inline: 'nearest',
          });
        }
      },
    );
  }

  get focusedIndex() {
    const currentIndex = this.state.options.findIndex(
      (c) => c.value === this.state.focusedValue,
    );

    if (currentIndex === -1) {
      return 0;
    }

    return currentIndex;
  }

  get isOpen() {
    return !!this.getOptionsBox();
  }

  close(selectValue = true) {
    this.tooltipRef?.close();
    this.tooltipRef = null;
    this.#actualSearch('');

    if (selectValue) {
      let currentValue = this.state.options.find(
        (c) => c.value === this.state.focusedValue,
      );
      if (!currentValue) {
        currentValue = this.state.options.find((c) => !c.filtered);
      }

      this.setValue(currentValue?.value || '');
    }
  }

  getAutocompleteBox() {
    return document.querySelector<HTMLElement>(
      `[data-autocomplete-box="${this.state.id!}"]`,
    );
  }

  getOptionsBox() {
    return document.querySelector<HTMLElement>(
      `[data-autocomplete-options="${this.state.id!}"]`,
    );
  }

  getOptionElement(value: string) {
    return this.getOptionsBox()?.querySelector<HTMLElement>(
      `.autocomplete__option[data-value="${value}"]`,
    );
  }

  getOptionByValue(value: string) {
    return this.state.options.find((current) => current.value === value);
  }

  focusFirst() {
    const option = this.state.options.find((c) => !c.filtered);

    if (option) {
      if (this.isOpen) {
        this.state.focusedValue = option.value;
        this.state.showValue = option.label?.toString?.() || option.value;
      } else {
        this.setValue(option.value);
      }
    }
  }

  focusLast() {
    const option = this.state.options.findLast((c) => !c.filtered);

    if (option) {
      if (this.isOpen) {
        this.state.focusedValue = option.value;
        this.state.showValue = option.label?.toString?.() || option.value;
      } else {
        this.setValue(option.value);
      }
    }
  }

  focusNext(n = 1) {
    const option = this.state.options[this.focusedIndex];
    if (option) {
      let newFocusedOption: AutocompleteOption = option;

      for (let i = this.focusedIndex + 1; i < this.state.options.length; i++) {
        if (
          !this.state.options[i].filtered &&
          (--n === 0 || i === this.state.options.length - 1)
        ) {
          newFocusedOption = this.state.options[i];
          break;
        }
      }

      if (this.isOpen) {
        this.state.focusedValue = newFocusedOption.value;
        this.state.showValue =
          newFocusedOption.label?.toString?.() || newFocusedOption.value;
      } else {
        this.setValue(newFocusedOption.value);
      }
    }
  }

  focusPrevious(n = 1) {
    const option = this.state.options[this.focusedIndex];
    if (option) {
      let newFocusedOption: AutocompleteOption = option;

      for (let i = this.focusedIndex - 1; i >= 0; i--) {
        if (!this.state.options[i].filtered && (--n === 0 || i === 0)) {
          newFocusedOption = this.state.options[i];
          break;
        }
      }

      if (this.isOpen) {
        this.state.focusedValue = newFocusedOption.value;
        this.state.showValue =
          newFocusedOption.label?.toString?.() || newFocusedOption.value;
      } else {
        this.setValue(newFocusedOption.value);
      }
    }
  }

  open() {
    if (this.state.disabled) {
      return;
    }

    this.tooltipRef = ApiaUtil.instance.tooltips.open({
      attachToElement: () => {
        return this.getAutocompleteBox()!;
      },
      closeOnMouseLeaveAttachedElement: false,
      closeOnMouseLeaveTooltip: false,
      closeOnClick: true,
      children: (
        <AutocompleteContext.Provider value={this}>
          <OptionsBox />
        </AutocompleteContext.Provider>
      ),
      onClose: () => {
        this.tooltipRef = null;
        this.close();
      },
      preferredOrientationY: 'bottom',
      preferredOrientationX: 'right',
      attachToElementAnchorPoint: 'bottomLeft',
      variant: 'layout.common.components.autocomplete.optionsContainer',
      className: 'autocomplete__options__box',
      'data-autocomplete-options': this.state.id!,
    });
  }

  toggleOpen() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  #actualSearch = (str: string) => {
    this.state.options.forEach((c) => {
      if (this.state.caseInsensitive) {
        c.filtered =
          (c.searchLabel ?? c.label?.toString?.() ?? c.value)
            .toLowerCase()
            .indexOf(str.toLowerCase()) === -1;
      } else {
        c.filtered =
          (c.searchLabel ?? c.label?.toString?.() ?? c.value)?.indexOf(str) ===
          -1;
      }

      if (this.getOptionByValue(this.state.focusedValue!)?.filtered) {
        this.state.focusedValue =
          this.state.options.find((c) => !c.filtered)?.value || null;
      }
    });
  };
  #makeSearch = () => {
    return debounce(this.#actualSearch, this.state.searchDebounce);
  };
  #search = this.#makeSearch();
  search(str: string) {
    this.#search(str);
  }

  selectFocused() {
    this.close();
    const newValue = this.state.focusedValue || this.state.options[0]?.value;

    if (newValue) {
      this.setValue(newValue);
    }
  }

  setValue(value: string) {
    const selectedOption = this.state.options.find((c) => c.value === value);

    this.state.value = selectedOption?.value || '';
    this.state.focusedValue = this.state.value;
    this.state.showValue =
      selectedOption?.label?.toString?.() || selectedOption?.value || '';
    this.state.onChange?.(this.state.value, this);
  }

  tooltipRef: ApiaUtilTooltip | null = null;
}
